import { cva, VariantProps } from 'class-variance-authority';
import Link from 'next/link';
import {
    AnchorHTMLAttributes,
    ButtonHTMLAttributes,
    ForwardedRef,
    forwardRef,
    PropsWithChildren,
    ReactNode
} from 'react';
import { UrlObject } from 'url';

export const button = cva(
    'tw-border-none tw-bg-transparent tw-text-accents-8 tw-py-2 tw-px-3 tw-w-full tw-text-left tw-flex tw-gap-3 tw-items-center tw-transition-colors tw-duration-200 tw-ease-in-out focus:tw-outline-none tw-rounded-lg',
    {
        variants: {
            disabled: {
                true: 'tw-cursor-not-allowed !hover:tw-bg-transparent !focus:tw-bg-transparent !tw-bg-transparent !tw-text-accents-3',
                else: 'focus:tw-bg-accents-2 tw-cursor-pointer'
            },
            color: {
                success: 'hover:tw-text-success tw-text-success focus:tw-bg-successLight hover:tw-bg-successLight',
                light: 'hover:tw-text-accents-9 tw-text-accents-9 focus:tw-bg-accents-2 hover:tw-bg-accents-2',
                error: 'hover:tw-text-red-500 tw-text-red-500 focus:tw-bg-red-100 hover:tw-bg-red-100',
                danger: 'hover:tw-text-red-500 tw-text-red-500 focus:tw-bg-red-100 hover:tw-bg-red-100',
                warning: 'hover:tw-text-yellow-500 tw-text-yellow-500 focus:tw-bg-orange-100 hover:tw-bg-orange-100'
            },
            size: {
                // `xs` used to be missing, so the size DropdownMenu passes for its
                // densest menus matched nothing, no size class was emitted, and those
                // items fell back to the inherited 16px body size — a menu set larger
                // than the toolbar it drops out of.
                xs: 'tw-text-xs',
                sm: 'tw-text-xs',
                md: 'tw-text-sm',
                lg: 'tw-text-base'
            }
        },
        defaultVariants: {
            disabled: false,
            color: 'light',
            // 16px, which is what an item with no size class inherited before this
            // variant existed. Naming it changes nothing on screen and stops the
            // rendered size depending on whichever stylesheet the item lands in.
            size: 'lg'
        }
    }
);

type AnchorElementProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'color' | 'href'>;
type ButtonElementProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'disabled'>;

interface CommonProps extends VariantProps<typeof button> {
    label?: string;
    disabled?: boolean;
    icon?: ReactNode;
}

export interface ButtonProps extends CommonProps, Omit<ButtonElementProps, keyof CommonProps> {
    link?: never;
    href?: never;
}

export interface LinkProps extends CommonProps, Omit<AnchorElementProps, keyof CommonProps> {
    link: true;
    href: UrlObject | string;
}

export type Props = LinkProps | ButtonProps;
type Ref = HTMLElement | HTMLAnchorElement;

const DropdownItem = forwardRef<Ref, PropsWithChildren<Props>>(
    ({ label, link, disabled, className, size, icon, href, color, children, ...props }, ref) => {
        if (link === true)
            return (
                <Link
                    href={href as any}
                    {...(props as AnchorElementProps)}
                    className={button({ disabled, color, className })}
                    ref={ref as ForwardedRef<HTMLAnchorElement>}
                    role="menuitem"
                >
                    <>
                        {icon && <span className="tw-flex tw-min-w-[20px] tw-justify-center tw-gap-[2px]">{icon}</span>}
                        {children || label}
                    </>
                </Link>
            );

        if (link === undefined)
            return (
                <button
                    {...(props as ButtonElementProps)}
                    className={button({ disabled, color, className })}
                    ref={ref as ForwardedRef<HTMLButtonElement>}
                    role="menuitem"
                    disabled={disabled}
                >
                    <>
                        {icon && <span className="tw-flex tw-min-w-[20px] tw-justify-center tw-gap-[2px]">{icon}</span>}
                        {children || label}
                    </>
                </button>
            );

        return null;
    }
);

DropdownItem.displayName = 'DropdownItem';

export default DropdownItem;
